import { appearanceHash, type CharacterAppearance } from "@ghostland/shared";
import * as direct from "./c3dComposite.js";
import type { ComposeJob, ComposeResult } from "./c3dComposite.worker.js";

/**
 * Off-main-thread c3d compositing. Same API (and signatures) as c3dComposite, but the
 * heavy work runs in a dedicated Web Worker so a new player spawning in — or a burst of
 * them — never freezes the game. Callers get back a normal HTMLCanvasElement (drawn from
 * the worker's transferred ImageBitmap), so everything downstream (CanvasTexture, repeat
 * math, pixel hit-tests, bust cropping) is unchanged.
 *
 * Scheduling: jobs are queued on the main thread and posted ONE at a time, so they can be
 * (re)prioritized — the MAIN idle/walk/run atlas (what makes a character visible) always
 * beats attack/block/sit/jump/smoke sheets, which trail behind. Identical concurrent
 * requests (same look + held gear) coalesce into one composite.
 *
 * Falls back to the direct main-thread compositor when Worker/OffscreenCanvas is
 * unavailable or the worker dies.
 */

type AtlasKind = "main" | "attack" | "block" | "sit" | "jump";

const DIRECT: Record<AtlasKind, (a: CharacterAppearance, w: boolean, s: boolean) => Promise<HTMLCanvasElement | null>> = {
  main: direct.compositeAtlas,
  attack: direct.compositeAttackAtlas,
  block: direct.compositeBlockAtlas,
  sit: direct.composeSitAtlas,
  jump: direct.composeJumpAtlas,
};

let worker: Worker | null | undefined; // undefined = not tried yet, null = unavailable/dead
let nextId = 1;
const pending = new Map<number, (bitmap: ImageBitmap | null) => void>();
const queue: { priority: number; send: () => void }[] = [];
const inflight = new Map<string, Promise<HTMLCanvasElement | null>>();

/** The worker died (or failed to boot): resolve everything in flight with null so each
 *  caller reruns on the main thread, and drain the queue the same way. */
function failWorker(): void {
  const w = worker;
  worker = null;
  w?.terminate();
  const waiting = [...pending.values()];
  pending.clear();
  for (const done of waiting) done(null);
  pump();
}

function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    if (typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined") throw new Error("unsupported");
    worker = new Worker(new URL("./c3dComposite.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (e: MessageEvent<ComposeResult>) => {
      const done = pending.get(e.data.id);
      pending.delete(e.data.id);
      done?.(e.data.bitmap);
      pump();
    };
    worker.onerror = () => failWorker();
  } catch {
    worker = null;
  }
  return worker;
}

/** Post the next queued job (lowest priority number first) once the worker is free. */
function pump(): void {
  while (pending.size === 0 && queue.length > 0) {
    queue.sort((x, y) => x.priority - y.priority); // the queue stays tiny — a sort per pump is nothing
    queue.shift()!.send(); // posts to the worker (pending grows, loop exits) or resolves directly (loop on)
  }
}

/** The transferred worker bitmap as a plain canvas — keeps every consumer unchanged. One
 *  GPU-side drawImage (a few ms even at 5 MP), nothing per-pixel. */
function bitmapToCanvas(bitmap: ImageBitmap): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = bitmap.width;
  c.height = bitmap.height;
  c.getContext("2d")!.drawImage(bitmap, 0, 0);
  bitmap.close();
  return c;
}

function runJob(kind: AtlasKind, a: CharacterAppearance, weapon: boolean, shield: boolean, priority: number): Promise<HTMLCanvasElement | null> {
  if (!getWorker()) return DIRECT[kind](a, weapon, shield);
  const key = `${kind}|${appearanceHash(a)}|${weapon ? 1 : 0}${shield ? 1 : 0}`;
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = new Promise<HTMLCanvasElement | null>((resolve) => {
    const send = (): void => {
      const w = getWorker();
      if (!w) { void DIRECT[kind](a, weapon, shield).then(resolve); return; } // worker died while queued
      const id = nextId++;
      pending.set(id, (bitmap) => {
        if (bitmap) resolve(bitmapToCanvas(bitmap));
        else if (!worker) void DIRECT[kind](a, weapon, shield).then(resolve); // worker died mid-job
        else resolve(null); // clean failure (e.g. missing sheet) — same contract as the direct path
      });
      const job: ComposeJob = { id, kind, a, weapon, shield };
      w.postMessage(job);
    };
    queue.push({ priority, send });
    pump();
  });
  const tracked = p.finally(() => inflight.delete(key));
  inflight.set(key, tracked);
  return tracked;
}

/** The main idle/walk/run atlas — the one that makes a character visible, so it's priority 0. */
export function compositeAtlas(a: CharacterAppearance, hasWeapon = false, hasShield = false): Promise<HTMLCanvasElement | null> {
  return runJob("main", a, hasWeapon, hasShield, 0);
}

export function compositeAttackAtlas(a: CharacterAppearance, hasWeapon = true, hasShield = true): Promise<HTMLCanvasElement | null> {
  return runJob("attack", a, hasWeapon, hasShield, 1);
}

export function compositeBlockAtlas(a: CharacterAppearance, hasWeapon = false, hasShield = false): Promise<HTMLCanvasElement | null> {
  return runJob("block", a, hasWeapon, hasShield, 1);
}

export function composeSitAtlas(a: CharacterAppearance, hasWeapon = false, hasShield = false): Promise<HTMLCanvasElement | null> {
  return runJob("sit", a, hasWeapon, hasShield, 1);
}

export function composeJumpAtlas(a: CharacterAppearance, hasWeapon = false, hasShield = false): Promise<HTMLCanvasElement | null> {
  return runJob("jump", a, hasWeapon, hasShield, 1);
}

/** Smoke rides on an ALREADY-composited main atlas: snapshot it as an ImageBitmap (cheap,
 *  async) and let the worker draw the cigarette/plume overlay. Lowest priority — it's a
 *  cosmetic idle variant. */
export async function composeSmokeAtlas(composited: HTMLCanvasElement): Promise<HTMLCanvasElement | null> {
  if (!getWorker()) return direct.composeSmokeAtlas(composited);
  const source = await createImageBitmap(composited);
  return new Promise<HTMLCanvasElement | null>((resolve) => {
    const send = (): void => {
      const w = getWorker();
      if (!w) { source.close(); void direct.composeSmokeAtlas(composited).then(resolve); return; }
      const id = nextId++;
      pending.set(id, (bitmap) => {
        if (bitmap) resolve(bitmapToCanvas(bitmap));
        else if (!worker) void direct.composeSmokeAtlas(composited).then(resolve);
        else resolve(null);
      });
      const job: ComposeJob = { id, kind: "smoke", source };
      w.postMessage(job, [source]);
    };
    queue.push({ priority: 2, send });
    pump();
  });
}
